import type { DetectedSequencingPlatform } from '../sequencingPlatformDetection.ts'
import type { SequenceRead } from './sequenceHead.ts'
import { readFileStem } from '../fileNames.ts'

export type ReadLayout = 'PAIRED' | 'MERGED_OR_SINGLE' | 'UNKNOWN'

/** `names-only`: no read contradicted or confirmed the file names. */
export type ReadLayoutConfidence = 'high' | 'names-only' | 'none'

export type ReadLayoutFile = {
  name: string
  /** Bytes; carried for callers, no detector relies on it. */
  size?: number
  /** The file's first reads; omitted when only names are known. */
  reads?: readonly SequenceRead[]
  platform?: DetectedSequencingPlatform
}

export type ReadLayoutResult = {
  layout: ReadLayout
  confidence: ReadLayoutConfidence
  reason: string
  /** Mates matched by name or by shared read ids, as indexes into the input. */
  pairs: [number, number][]
  /** Per pair, what the reads say about it; `unchecked` when names alone paired them. */
  pairChecks: PairCheck[]
  unpaired: number[]
  /**
   * No layout fits — no file pairs up, or the name pairs are vetoed or a minority — and the files are mates
   * of this number, or merged reads: the lab says which. `rawLength`: the fixed instrument length most
   * files' reads have, when they have one.
   */
  unmated?: Unmated
}

export type Unmated = { mate: 1 | 2; rawLength?: number }

type Mate = 1 | 2

type MateToken = { family: string; pattern: RegExp; mate: (token: string) => Mate }

/*
 * Each family finds the last token naming a mate. Families are tried separately and the one pairing the
 * most files wins, so a sample literally named `…_1` cannot pair with `…_2` when the batch also carries
 * `_R1`/`_R2`. The bare `_1`/`_2` family is what SRA dumps and some core facilities write.
 */
const MATE_TOKENS: MateToken[] = [
  { family: 'R1/R2', pattern: /(^|[._-])(R[12])(?=$|[._-])/gi, mate: (t) => (t.endsWith('1') ? 1 : 2) },
  { family: 'fwd/rev', pattern: /(^|[._-])(fwd|rev|forward|reverse|ft|rt)(?=$|[._-])/gi, mate: (t) => (/^f/i.test(t) ? 1 : 2) },
  { family: 'F/R', pattern: /(^|[._-])([FR])(?=$|[._-])/gi, mate: (t) => (/F/i.test(t) ? 1 : 2) },
  { family: '1/2', pattern: /([._-])([12])$/g, mate: (t) => (t === '1' ? 1 : 2) }
]

const mateKey = (stem: string, token: MateToken): { key: string; mate: Mate } | null => {
  let last: RegExpExecArray | null = null
  token.pattern.lastIndex = 0
  for (let match = token.pattern.exec(stem); match; match = token.pattern.exec(stem)) {
    last = match
    if (match[0].length === 0) {
      token.pattern.lastIndex++
    }
  }
  if (!last) {
    return null
  }
  const value = last[2]
  const start = last.index + last[1].length
  return { key: `${stem.slice(0, start)}\u0000${stem.slice(start + value.length)}`.toLowerCase(), mate: token.mate(value) }
}

type Pairing = { family: string; pairs: [number, number][] }

export const nameMate = (name: string): Mate | null => {
  const stem = readFileStem(name)
  for (const token of MATE_TOKENS) {
    const found = mateKey(stem, token)
    if (found) {
      return found.mate
    }
  }
  return null
}

const pairByNames = (names: readonly string[]): Pairing => {
  let best: Pairing = { family: 'none', pairs: [] }
  for (const token of MATE_TOKENS) {
    const groups = new Map<string, { 1: number[]; 2: number[] }>()
    names.forEach((name, index) => {
      const found = mateKey(readFileStem(name), token)
      if (!found) {
        return
      }
      const group = groups.get(found.key) ?? { 1: [], 2: [] }
      group[found.mate].push(index)
      groups.set(found.key, group)
    })
    const pairs: [number, number][] = []
    for (const group of groups.values()) {
      if (group[1].length === 1 && group[2].length === 1) {
        pairs.push([group[1][0], group[2][0]])
      }
    }
    if (pairs.length > best.pairs.length) {
      best = { family: token.family, pairs }
    }
  }
  return best
}

/** Longer than the 2×300 chemistries; reads beyond this are merged unless they come in confirmed mate pairs. */
const MAX_RAW_ILLUMINA_BP = 302
const MERGED_LONG_SHARE = 0.2
const RAW_READ_LENGTHS = new Set([36, 50, 51, 75, 76, 100, 101, 125, 126, 150, 151, 250, 251, 300, 301, 500, 501])
const RAW_MODE_SHARE = 0.6
const MIN_READS = 10
const ID_OVERLAP_CONFIRMS = 0.5
/** A few orphans (a mate not selected, a re-upload) should not hide an otherwise paired batch. */
const MIN_PAIRED_SHARE = 0.8
/** Below the paired share, still more paired files than orphans: the pairs hold, the orphans wait for a mate. */
const ORPHANS_AT_MOST_HALF = 0.5

type FileEvidence = {
  readNumber: Mate | null
  ids: Set<string>
  /** Instrument-coordinate ids are unique per cluster; renamed ids (`SM1|SM1_7`) repeat across files. */
  coordinateIds: boolean
  mergedLength: boolean
  rawLength: boolean
  modeLength: number | null
  hasReads: boolean
}

const ILLUMINA_COMMENT = /^\S+\s+([12]):[YN]:/
const LEGACY_MATE = /\/([12])$/

const readNumberOf = (header: string): Mate | null => {
  const comment = ILLUMINA_COMMENT.exec(header)
  if (comment) {
    return comment[1] === '1' ? 1 : 2
  }
  const legacy = LEGACY_MATE.exec(header.split(/\s+/)[0] ?? '')
  if (legacy) {
    return legacy[1] === '1' ? 1 : 2
  }
  return null
}

const evidenceOf = (reads: readonly SequenceRead[] | undefined): FileEvidence => {
  if (!reads || reads.length < MIN_READS) {
    return {
      readNumber: null,
      ids: new Set(),
      coordinateIds: false,
      mergedLength: false,
      rawLength: false,
      modeLength: null,
      hasReads: false
    }
  }
  const numbers = { 1: 0, 2: 0 }
  const lengths = new Map<number, number>()
  let long = 0
  const ids = new Set<string>()
  for (const read of reads) {
    const number = readNumberOf(read.header)
    if (number) {
      numbers[number]++
    }
    ids.add((read.header.split(/\s+/)[0] ?? '').replace(LEGACY_MATE, ''))
    const length = read.sequence.length
    lengths.set(length, (lengths.get(length) ?? 0) + 1)
    if (length > MAX_RAW_ILLUMINA_BP) {
      long++
    }
  }
  const [modeLength, modeCount] = [...lengths.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))
  const longerThanMode = reads.filter((read) => read.sequence.length > modeLength).length
  const readNumber: Mate | null = numbers[1] > reads.length / 2 ? 1 : numbers[2] > reads.length / 2 ? 2 : null
  return {
    readNumber,
    ids,
    coordinateIds: [...ids].every((id) => id.split(':').length >= 5),
    mergedLength: long / reads.length >= MERGED_LONG_SHARE,
    rawLength: RAW_READ_LENGTHS.has(modeLength) && modeCount / reads.length >= RAW_MODE_SHARE && longerThanMode === 0,
    modeLength,
    hasReads: true
  }
}

/**
 * Mates carry the same read ids in the same order, so files the names left alone can still be paired
 * by their reads — the only signal left when a pipeline or a download renamed the files.
 */
const pairByReadIds = (evidence: readonly FileEvidence[], taken: ReadonlySet<number>): [number, number][] => {
  const pairs: [number, number][] = []
  const used = new Set(taken)
  for (let a = 0; a < evidence.length; a++) {
    if (used.has(a) || !evidence[a].coordinateIds) {
      continue
    }
    for (let b = a + 1; b < evidence.length; b++) {
      if (used.has(b) || !evidence[b].coordinateIds) {
        continue
      }
      const shared = [...evidence[a].ids].filter((id) => evidence[b].ids.has(id)).length
      if (shared / Math.min(evidence[a].ids.size, evidence[b].ids.size) >= ID_OVERLAP_CONFIRMS) {
        pairs.push(evidence[a].readNumber === 2 ? [b, a] : [a, b])
        used.add(a)
        used.add(b)
        break
      }
    }
  }
  return pairs
}

export type PairCheck = 'confirmed' | 'contradicted' | 'unchecked'

const checkPair = (a: FileEvidence, b: FileEvidence): PairCheck => {
  if (!a.hasReads || !b.hasReads) {
    return 'unchecked'
  }
  const shared = [...a.ids].filter((id) => b.ids.has(id)).length
  const overlap = shared / Math.min(a.ids.size, b.ids.size)
  const matesNumbered = a.readNumber !== null && b.readNumber !== null && a.readNumber !== b.readNumber
  if (overlap >= ID_OVERLAP_CONFIRMS || matesNumbered) {
    return 'confirmed'
  }
  /* two first mates, or two second mates, that share no read are two samples, not one pair */
  if (overlap === 0 && a.readNumber !== null && a.readNumber === b.readNumber) {
    return 'contradicted'
  }
  return 'unchecked'
}

/**
 * Whether an upload batch is paired-end (R1+R2 per sample) or one file per sample (merged, or single
 * ended as Nanopore always is). Names propose the pairs; reads, when given, confirm or veto them.
 * Anything the signals do not agree on stays `UNKNOWN`.
 */
export const detectReadLayout = (files: readonly ReadLayoutFile[]): ReadLayoutResult => {
  const result = layoutOf(files)
  const evidence = files.map((file) => evidenceOf(file.reads))
  return { ...result, pairChecks: result.pairs.map(([a, b]) => checkPair(evidence[a], evidence[b])) }
}

const commonest = (values: readonly number[]): number => {
  const counts = new Map<number, number>()
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1))
  return [...counts].reduce((a, b) => (b[1] > a[1] ? b : a))[0]
}

const layoutOf = (files: readonly ReadLayoutFile[]): Omit<ReadLayoutResult, 'pairChecks'> => {
  const none = (reason: string, pairs: [number, number][] = [], unpaired: number[] = []): Omit<ReadLayoutResult, 'pairChecks'> => ({
    layout: 'UNKNOWN',
    confidence: 'none',
    reason,
    pairs,
    unpaired
  })
  if (files.length === 0) {
    return none('no files')
  }
  if (files.some((file) => file.platform === 'ONT')) {
    return {
      layout: 'MERGED_OR_SINGLE',
      confidence: 'high',
      reason: 'Nanopore reads are single-ended',
      pairs: [],
      unpaired: files.map((_, i) => i)
    }
  }

  const evidence = files.map((file) => evidenceOf(file.reads))
  const byName = pairByNames(files.map((file) => file.name))
  const nameIndexes = new Set(byName.pairs.flat())
  const byReads = pairByReadIds(evidence, nameIndexes)
  const pairs = [...byName.pairs, ...byReads]
  const family =
    byReads.length === 0
      ? `${byName.family} names`
      : byName.pairs.length === 0
        ? 'shared read ids'
        : `${byName.family} names and shared read ids`
  const pairedIndexes = new Set(pairs.flat())
  const unpaired = files.map((_, i) => i).filter((i) => !pairedIndexes.has(i))
  const merged = evidence.filter((e) => e.mergedLength).length
  const pairedShare = pairedIndexes.size / files.length
  const withReads = evidence.filter((e) => e.hasReads)
  const raw = withReads.filter((e) => e.rawLength)
  const rawLength = raw.length > withReads.length / 2 ? commonest(raw.map((e) => e.modeLength as number)) : undefined
  const unmated = (mate: 1 | 2): Unmated => (rawLength ? { mate, rawLength } : { mate })
  /* no pairing to go by: whether partner files are missing or the reads are merged is the lab's to say */
  const unsettled = (reason: string) => {
    const mates = files.map((file, i) => evidence[i].readNumber ?? nameMate(file.name))
    const second = mates.filter((mate) => mate === 2).length > mates.filter((mate) => mate === 1).length
    return { ...none(reason, pairs, unpaired), unmated: unmated(second ? 2 : 1) }
  }

  if (pairs.length > 0) {
    const checks = byName.pairs.map(([a, b]) => checkPair(evidence[a], evidence[b]))
    if (checks.includes('contradicted')) {
      return unsettled(`names pair up (${byName.family}) but the reads of a pair are not mates`)
    }
    /* mates sharing read ids are raw reads whatever their length (MiSeq i100 reads 2×500); only unconfirmed pairs are vetoed */
    const unconfirmedLong = byName.pairs.filter(
      ([a, b], i) => checks[i] !== 'confirmed' && (evidence[a].mergedLength || evidence[b].mergedLength)
    ).length
    if (unconfirmedLong > 0) {
      return unsettled(`files pair up by ${family} but ${unconfirmedLong} unconfirmed pair(s) hold reads longer than a raw read`)
    }
    const fewPair = `only ${pairedIndexes.size} of ${files.length} files pair up`
    if (pairedShare <= ORPHANS_AT_MOST_HALF) {
      return unsettled(fewPair)
    }
    if (pairedShare < MIN_PAIRED_SHARE) {
      return none(fewPair, pairs, unpaired)
    }
    const confirmed = checks.filter((c) => c === 'confirmed').length + byReads.length
    const orphans = unpaired.length > 0 ? `; ${unpaired.length} file(s) without a mate` : ''
    return {
      layout: 'PAIRED',
      confidence: confirmed > 0 ? 'high' : 'names-only',
      reason: `${pairs.length} pair(s) by ${family}` + (confirmed > 0 ? `, ${confirmed} confirmed by reads` : '') + orphans,
      pairs,
      unpaired
    }
  }
  if (withReads.some((e) => e.readNumber === 2)) {
    return unsettled('no files pair up, but some hold second-mate (R2) reads')
  }
  if (withReads.length > 0 && merged > withReads.length / 2) {
    return { layout: 'MERGED_OR_SINGLE', confidence: 'high', reason: 'no mates, reads longer than raw Illumina reads', pairs, unpaired }
  }
  if (rawLength) {
    return {
      ...none('no mates, but the reads look raw (fixed instrument length) — R1 files without their R2?', pairs, unpaired),
      unmated: unmated(1)
    }
  }
  /* varying lengths suggest merged reads, which keep their R1 headers; R1 files whose R2 are missing look the same */
  if (files.some((file, i) => (evidence[i].readNumber ?? nameMate(file.name)) !== null)) {
    return unsettled('no mates, read lengths vary like merged reads, but the names or headers mark mates')
  }
  return {
    layout: 'MERGED_OR_SINGLE',
    confidence: withReads.length > 0 ? 'high' : 'names-only',
    reason: withReads.length > 0 ? 'no mates, read lengths vary like merged reads' : 'no mates by name',
    pairs,
    unpaired
  }
}
